// ============================================================================
// Phase 3: Role Types & Tech Stack Mapping
// Enterprise-grade technical interview configuration
// Used by: CompanyAdminDashboard, MasterAdminDashboard, UserDashboard
// ============================================================================

export const ROLE_TECH_MAP = {

  // ── Software Engineering ──────────────────────────────────────────────────
  "Software Engineer - Backend": [
    "Java", "Python", "C++", "C# / .NET", "Go", "Rust", "Node.js",
    "Ruby", "PHP", "Scala", "Kotlin (Server)", "Elixir"
  ],
  "Software Engineer - Frontend": [
    "React", "Angular", "Vue.js", "Svelte", "Next.js", "TypeScript",
    "HTML / CSS / JavaScript", "Redux / State Management", "Tailwind CSS",
    "Webpack / Vite", "Accessibility (WCAG)"
  ],
  "Software Engineer - Full Stack": [
    "React + Node.js (MERN)", "React + Python (Django/FastAPI)",
    "Angular + Java (Spring Boot)", "Vue.js + Go",
    "Next.js + PostgreSQL", "Ruby on Rails + React",
    "Django + React", "MEAN Stack", "T3 Stack (Next/tRPC/Prisma)"
  ],
  "Software Engineer - Mobile (iOS)": [
    "Swift", "SwiftUI", "Objective-C", "UIKit", "Core Data",
    "Combine / Async-Await", "XCTest"
  ],
  "Software Engineer - Mobile (Android)": [
    "Kotlin", "Java (Android)", "Jetpack Compose", "Android SDK",
    "Room / SQLite", "Coroutines / Flow", "Espresso / JUnit"
  ],
  "Software Engineer - Mobile (Cross-Platform)": [
    "React Native", "Flutter / Dart", "Xamarin / .NET MAUI",
    "Ionic / Capacitor", "Kotlin Multiplatform (KMP)"
  ],

  // ── SAS & Analytics ───────────────────────────────────────────────────────
  "SAS Developer": [
    "SAS 9.x", "SAS Viya", "SAS Enterprise Guide", "SAS Studio",
    "SAS Macro Language", "PROC SQL", "DATA Step Programming",
    "SAS/STAT", "SAS/GRAPH", "ODS Reporting",
    "ETL / ELT Pipelines", "SQL Server", "Oracle", "PostgreSQL",
    "Python (pandas)", "Shell Scripting", "Control-M / Autosys",
    "Git / Azure DevOps", "Power BI", "Tableau"
  ],
  "SAS Analyst": [
    "SAS Base Programming", "SAS Enterprise Guide", "PROC SQL",
    "SAS Macros", "Statistical Analysis", "SAS/STAT",
    "Data Cleansing & Validation", "Reporting & Dashboards",
    "Excel / Advanced Excel", "Power BI", "Tableau",
    "SQL", "Data Modeling", "SAS Viya"
  ],
  "ETL Developer": [
    "Informatica PowerCenter", "Informatica Cloud (IICS)",
    "IBM DataStage", "Microsoft SSIS", "Talend",
    "Apache NiFi", "Oracle ODI", "Ab Initio",
    "SQL / T-SQL / PL-SQL", "Python", "Spark",
    "Snowflake", "Azure Data Factory", "AWS Glue",
    "Data Warehousing", "Dimensional Modeling"
  ],
  "Cognos Developer": [
    "IBM Cognos Analytics", "Cognos Framework Manager",
    "Cognos Report Studio", "Cognos Query Studio",
    "Cognos Transformer (OLAP)", "TM1 / Planning Analytics",
    "SQL / DB2 / Oracle", "Dimensional Modeling",
    "Metadata Modeling", "Drill-through / Drill-down Reports",
    "Burst Reports", "Dashboard Design"
  ],

  // ── Data, BI & Analytics ──────────────────────────────────────────────────
  "Data Engineer": [
    "SQL", "Python", "Apache Spark", "Apache Kafka", "Apache Airflow",
    "dbt", "Snowflake", "Databricks", "BigQuery", "Hadoop",
    "Redshift", "ETL / ELT Pipelines", "Data Modeling"
  ],
  "Data Scientist": [
    "Python (Pandas / NumPy)", "R", "SQL", "Scikit-learn",
    "TensorFlow", "PyTorch", "Jupyter Notebooks", "Statistical Modeling",
    "A/B Testing", "Data Visualization (Tableau / Power BI)"
  ],
  "Data Analyst": [
    "SQL", "Python (pandas)", "Excel / Advanced Excel",
    "Power BI", "Tableau", "Looker", "Google Data Studio",
    "Statistical Analysis", "Data Cleaning & Wrangling",
    "A/B Testing", "KPI Dashboards", "ETL Basics"
  ],
  "BI Developer / BI Specialist": [
    "Power BI", "Tableau", "Looker", "QlikView / Qlik Sense",
    "MicroStrategy", "IBM Cognos", "SSRS / SSAS / SSIS",
    "SQL / T-SQL", "DAX", "MDX",
    "Data Warehousing", "Dimensional Modeling (Star / Snowflake)",
    "ETL Pipelines", "Azure Synapse", "Snowflake"
  ],
  "Business Analyst (Technical)": [
    "Requirements Gathering", "Business Process Modeling (BPMN)",
    "Use Case & User Story Writing", "SQL",
    "JIRA / Confluence", "Visio / Lucidchart",
    "Agile / Scrum", "UAT Coordination",
    "Excel / Power BI", "API Integration Basics",
    "Stakeholder Management", "Gap Analysis"
  ],
  "ML Engineer / AI Engineer": [
    "Python (ML)", "TensorFlow", "PyTorch", "Hugging Face / Transformers",
    "MLOps (MLflow / Kubeflow)", "LLM / GenAI", "Computer Vision",
    "NLP / NLU", "Recommendation Systems", "Model Deployment (SageMaker / Vertex AI)"
  ],

  // ── QA & Testing ─────────────────────────────────────────────────────────
  "QA Engineer - Manual": [
    "Manual Test Case Design", "Test Planning & Strategy",
    "Functional Testing", "Regression Testing", "Smoke / Sanity Testing",
    "UAT Coordination", "Bug Tracking (JIRA / Azure DevOps)",
    "TestRail / Zephyr / Xray", "API Testing (Postman)",
    "Database / SQL Testing", "Cross-browser / Cross-platform Testing",
    "Agile / Scrum Testing Practices"
  ],
  "QA Engineer - Automation": [
    "Selenium WebDriver", "Playwright", "Cypress",
    "Appium (Mobile Automation)", "Rest Assured / API Automation",
    "JUnit / TestNG / pytest", "BDD (Cucumber / SpecFlow / Behave)",
    "Java / Python / JavaScript (Automation)",
    "CI/CD Integration (Jenkins / GitHub Actions)", "TestRail / Xray",
    "Performance Testing (JMeter / Gatling / k6)",
    "Page Object Model (POM)", "Git"
  ],
  "QA Engineer / SDET": [
    "Selenium", "Cypress", "Playwright", "JUnit / TestNG",
    "pytest", "Postman / API Testing", "JMeter / Performance Testing",
    "TestRail / Xray", "BDD (Cucumber / SpecFlow)",
    "Mobile Testing (Appium)", "CI/CD Test Integration"
  ],
  "Performance Test Engineer": [
    "JMeter", "Gatling", "k6", "Locust",
    "LoadRunner / NeoLoad", "BlazeMeter",
    "Performance Metrics Analysis", "Bottleneck Identification",
    "SQL Query Optimization", "APM Tools (Dynatrace / AppDynamics / New Relic)",
    "Linux / Server Monitoring", "Scripting (Python / Groovy)"
  ],
  "QA Lead / QA Manager": [
    "Test Strategy & Planning", "Team Leadership",
    "Test Process Improvement", "Risk-Based Testing",
    "Metrics & Reporting (Defect Density / Test Coverage)",
    "Manual + Automation Oversight", "Agile / Scrum",
    "Stakeholder Communication", "Tool Selection",
    "Release Management", "Compliance Testing (SOX / HIPAA)"
  ],

  // ── IT Infrastructure ─────────────────────────────────────────────────────
  "IT Administrator": [
    "Windows Server (Active Directory / Group Policy)",
    "Office 365 / Microsoft 365 Admin",
    "Azure AD / Entra ID", "LDAP / DNS / DHCP",
    "Help Desk / ITSM (ServiceNow / Jira)", "Virtualization (VMware / Hyper-V)",
    "Backup & Recovery", "Endpoint Management (Intune / SCCM)",
    "Networking Fundamentals (TCP/IP)", "PowerShell Scripting"
  ],
  "System Administrator": [
    "Windows Server Administration", "Linux Administration",
    "Active Directory", "DNS / DHCP / WINS",
    "VMware vSphere / Hyper-V", "Storage (SAN / NAS)",
    "Backup Solutions (Veeam / Commvault)", "PowerShell / Bash",
    "Patch Management", "ITSM (ServiceNow)",
    "Monitoring (Nagios / Zabbix / PRTG)",
    "Disaster Recovery Planning"
  ],
  "Windows Administrator": [
    "Windows Server 2016/2019/2022", "Active Directory & Group Policy",
    "Azure AD / Entra ID / Hybrid Identity",
    "IIS / Web Services", "DNS / DHCP / WINS",
    "PowerShell Scripting", "SCCM / Intune / MECM",
    "Exchange / Office 365", "Hyper-V",
    "Patch Management (WSUS)", "Backup & Recovery"
  ],
  "Linux Administrator": [
    "RHEL / CentOS / Rocky Linux", "Ubuntu / Debian",
    "Bash / Shell Scripting", "Python (Automation)",
    "File System Management", "LVM / RAID",
    "Networking (iptables / firewalld)", "Apache / Nginx",
    "SSH / SSL / PKI", "Ansible / Puppet / Chef",
    "Cron Jobs & Scheduling", "Performance Monitoring"
  ],
  "Network Engineer": [
    "Cisco (CCNA / CCNP)", "Juniper", "SD-WAN",
    "Firewall (Palo Alto / Fortinet / Check Point)", "Load Balancing (F5 / Nginx)",
    "DNS / DHCP", "BGP / OSPF / EIGRP", "MPLS / VPN",
    "Network Automation (Python / Ansible)",
    "Zero Trust Network Architecture", "Wireshark / Network Monitoring"
  ],
  "Network Security Engineer": [
    "Firewall Administration (Palo Alto / Fortinet / Check Point)",
    "IDS / IPS", "VPN (IPSec / SSL)", "Zero Trust Architecture",
    "Network Segmentation / Micro-segmentation",
    "SIEM (Splunk / Microsoft Sentinel)", "DDoS Mitigation",
    "PKI / Certificate Management", "Cisco ISE / NAC",
    "Cloud Network Security (AWS / Azure)", "Penetration Testing"
  ],
  "Cybersecurity Analyst": [
    "SOC Operations (L1 / L2 / L3)", "SIEM (Splunk / Sentinel / QRadar)",
    "Threat Intelligence", "Incident Response",
    "Vulnerability Management (Nessus / Qualys)",
    "Malware Analysis", "Phishing Investigation",
    "MITRE ATT&CK Framework", "Endpoint Detection & Response (EDR)",
    "Log Analysis", "OSINT"
  ],
  "Cybersecurity Developer / Engineer": [
    "Application Security (OWASP Top 10)", "Secure SDLC",
    "Penetration Testing", "DAST / SAST (Checkmarx / Veracode / Burp Suite)",
    "IAM / OAuth / SAML / OpenID Connect",
    "Secrets Management (HashiCorp Vault / Azure Key Vault)",
    "Container Security (Aqua / Prisma Cloud)",
    "Python / Go (Security Tooling)",
    "Cloud Security (AWS/Azure/GCP)", "Cryptography"
  ],
  "Cybersecurity Architect": [
    "Zero Trust Architecture", "Security Frameworks (NIST / ISO 27001 / CIS)",
    "Enterprise Security Design", "Identity Architecture (IAM / PAM)",
    "Cloud Security Architecture (AWS / Azure / GCP)",
    "Data Security & Privacy (GDPR / HIPAA)",
    "Threat Modeling (STRIDE / PASTA)",
    "Security Governance & Risk Management",
    "Compliance (SOC2 / PCI-DSS)", "Security Roadmapping"
  ],
  "Security Engineer": [
    "Application Security (OWASP)", "Network Security",
    "Cloud Security (AWS/Azure/GCP)", "Penetration Testing",
    "SIEM (Splunk / Sentinel)", "SOC Operations",
    "Identity & Access Management (IAM)", "Cryptography",
    "Compliance (SOC2 / ISO 27001)", "Threat Modeling"
  ],

  // ── DevOps & Cloud ────────────────────────────────────────────────────────
  "DevOps Engineer": [
    "AWS", "Azure", "GCP", "Docker", "Kubernetes",
    "Terraform", "Ansible", "Jenkins", "GitHub Actions",
    "CI/CD Pipelines", "Linux Administration", "Helm"
  ],
  "Site Reliability Engineer (SRE)": [
    "Linux", "Kubernetes", "Prometheus / Grafana", "Go", "Python",
    "Bash Scripting", "Incident Management", "Chaos Engineering",
    "Observability (Datadog / Splunk)", "SLI / SLO / SLA"
  ],
  "Cloud Engineer / Cloud Architect": [
    "AWS (Solutions Architect)", "Azure (Cloud Architect)", "GCP (Cloud Architect)",
    "Multi-Cloud Strategy", "Serverless (Lambda / Functions)",
    "CloudFormation / ARM Templates / Deployment Manager",
    "Cost Optimization", "Cloud Security", "Landing Zone Design"
  ],
  "Platform Engineer": [
    "Kubernetes", "Docker", "Service Mesh (Istio / Linkerd)",
    "Internal Developer Platforms", "Go", "Rust",
    "GitOps (ArgoCD / Flux)", "API Gateway", "Developer Experience Tooling"
  ],

  // ── ERP ───────────────────────────────────────────────────────────────────
  "SAP ABAP Developer": [
    "ABAP Programming", "ABAP Objects (OOP)",
    "SAP HANA / CDS Views", "BAPI / RFC / Function Modules",
    "SAP Fiori / UI5", "Workflow Development",
    "SAP Enhancement Framework (BAdI / User Exits)",
    "SAP S/4HANA Migration", "ALE / EDI / IDoc",
    "SAP Reports (ALV)", "Transport Management System (TMS)"
  ],
  "SAP Functional Consultant": [
    "SAP S/4HANA", "SAP ECC",
    "SAP FI / CO (Finance & Controlling)",
    "SAP SD (Sales & Distribution)",
    "SAP MM (Materials Management)",
    "SAP PP (Production Planning)",
    "SAP HCM / SuccessFactors",
    "SAP Business Process Configuration",
    "SAP Solution Manager", "SAP Activate Methodology",
    "Integration (SAP PI / CPI)"
  ],
  "SAP BI / BW Developer": [
    "SAP BW / BW/4HANA", "SAP Business Objects (BO)",
    "SAP Lumira / Analytics Cloud (SAC)",
    "Crystal Reports", "Web Intelligence (WebI)",
    "SAP HANA Modeling", "CDS Views",
    "BEx Query Designer", "Data Provisioning (DSO / InfoCube)",
    "SAP Data Services (BODS)", "ETL in SAP"
  ],
  "SAP Technical Architect": [
    "SAP S/4HANA Architecture", "SAP Landscape Design",
    "SAP Basis Administration", "SAP Integration (CPI / PI / PO)",
    "ABAP Advanced", "SAP Fiori / UI5",
    "SAP BTP (Business Technology Platform)",
    "SAP Security & Authorization",
    "Performance Tuning", "Upgrade & Migration Planning"
  ],
  "SAGE ERP Consultant": [
    "Sage 300 / Sage 500 / Sage X3",
    "Sage Intacct", "Sage CRM",
    "General Ledger / Accounts Payable / Receivable",
    "Sage Crystal Reports",
    "Sage Business Intelligence",
    "Payroll & HR Modules",
    "Data Migration", "System Configuration",
    "SQL Server (Sage backend)", "Integration APIs"
  ],
  "Oracle ERP Consultant": [
    "Oracle Fusion Cloud ERP", "Oracle E-Business Suite (EBS)",
    "Oracle Financials (GL / AP / AR)",
    "Oracle SCM / Procurement",
    "Oracle HCM Cloud", "Oracle BI / OBIEE",
    "OTBI Reports", "Fast Formula",
    "Oracle PL/SQL", "Integration (OIC / MuleSoft)"
  ],
  "Microsoft Dynamics Consultant": [
    "Microsoft Dynamics 365 (F&O / CE / BC)",
    "Dynamics AX / NAV / GP",
    "Power Platform (Power Apps / Power Automate)",
    "D365 Finance & Operations",
    "D365 Customer Engagement (CRM)",
    "Azure Integration Services",
    "X++ Development", "SSRS / Power BI Reports",
    "Lifecycle Services (LCS)", "Data Migration (DIXF)"
  ],

  // ── CRM ───────────────────────────────────────────────────────────────────
  "Salesforce Developer": [
    "Apex (OOP)", "Lightning Web Components (LWC)",
    "Visualforce", "SOQL / SOSL",
    "Salesforce REST / SOAP APIs",
    "Triggers & Batch Apex",
    "Salesforce DX / CLI / Git",
    "Integration (MuleSoft / Boomi)",
    "Sales Cloud / Service Cloud / Experience Cloud",
    "Marketing Cloud / Pardot",
    "CPQ / Billing", "Salesforce Flow / Process Builder"
  ],
  "Salesforce Administrator": [
    "User & Profile Management", "Roles & Permission Sets",
    "Salesforce Flow Automation", "Reports & Dashboards",
    "Data Management (Import / Export / Deduplication)",
    "Sales Cloud / Service Cloud Configuration",
    "AppExchange Management",
    "Validation Rules / Formula Fields",
    "Change Management", "Salesforce Certifications (ADM 201)"
  ],
  "Salesforce Analyst / Consultant": [
    "Salesforce CRM Strategy", "Business Requirements Analysis",
    "Sales Cloud / Service Cloud", "Marketing Cloud",
    "Salesforce Flow / Automation",
    "Reports & Dashboards", "Data Migration",
    "Integration Planning (MuleSoft / APIs)",
    "User Training & Adoption",
    "Salesforce CPQ", "Stakeholder Management"
  ],
  "HubSpot / CRM Specialist": [
    "HubSpot CRM", "HubSpot Marketing Hub / Sales Hub",
    "CRM Configuration & Customization",
    "Marketing Automation (Workflows)",
    "Lead Management & Scoring",
    "Email Marketing", "Reporting & Analytics",
    "CRM Data Migration",
    "API / Webhook Integrations",
    "Zoho CRM / Microsoft Dynamics CE"
  ],
  "Sage CRM Consultant": [
    "Sage CRM Configuration",
    "Sales / Marketing / Customer Service Modules",
    "Sage CRM Reporting & Dashboards",
    "Data Import / Migration",
    "Integration with Sage ERP",
    "Customization (ASP / .NET)",
    "Workflow Automation",
    "User Management", "Sage CRM APIs"
  ],

  // ── Web Technologies & UI/UX Design ──────────────────────────────────────
  "UI/UX Designer": [
    "Figma", "Adobe XD", "Sketch",
    "User Research & Usability Testing",
    "Wireframing & Prototyping",
    "Interaction Design", "Information Architecture",
    "Design Systems", "Accessibility (WCAG 2.1)",
    "User Journey Mapping", "A/B Testing",
    "HTML / CSS (Basic)", "Zeplin / InVision"
  ],
  "Graphic Designer": [
    "Adobe Creative Suite (Photoshop / Illustrator / InDesign)",
    "Figma / Sketch",
    "Brand Identity & Visual Design",
    "Typography & Color Theory",
    "Motion Graphics (After Effects)",
    "Print & Digital Design",
    "Social Media Design",
    "Video Editing (Premiere Pro)",
    "3D Design (Blender / Cinema 4D)"
  ],
  "UI Architect / Frontend Architect": [
    "React / Angular / Vue.js Architecture",
    "Micro-Frontend Architecture",
    "Design Systems (Storybook)",
    "Performance Optimization (Core Web Vitals)",
    "Accessibility (WCAG / ARIA)",
    "TypeScript", "CSS Architecture (BEM / Tailwind)",
    "State Management (Redux / Zustand / NgRx)",
    "SSR / SSG (Next.js / Nuxt.js)",
    "Web Security (CSP / XSS Prevention)",
    "Build Systems (Webpack / Vite / Turborepo)"
  ],
  "UX Engineer": [
    "React", "Design Systems", "Accessibility (WCAG / ARIA)",
    "CSS Architecture", "Animation (Framer Motion / GSAP)",
    "Figma-to-Code", "Performance Optimization",
    "Component Library Development"
  ],
  "Web Developer": [
    "HTML5 / CSS3 / JavaScript (ES6+)",
    "React / Vue.js / Angular",
    "WordPress / CMS",
    "PHP / Node.js", "SQL / MySQL",
    "RESTful APIs", "Responsive Design",
    "SEO Basics", "Web Performance",
    "Git / Version Control"
  ],

  // ── Architecture & Technical Leadership ───────────────────────────────────
  "Solutions Architect": [
    "Enterprise Architecture", "Microservices Architecture",
    "Event-Driven Architecture", "API Design (REST / GraphQL / gRPC)",
    "System Design", "AWS SA Professional", "Azure SA Expert",
    "GCP Professional Cloud Architect", "Domain-Driven Design (DDD)"
  ],
  "Systems / Infrastructure Engineer": [
    "Linux Administration", "Windows Server", "VMware / Virtualization",
    "Storage Systems (SAN / NAS)", "Networking (TCP/IP / DNS / Load Balancing)",
    "Configuration Management (Puppet / Chef)", "Monitoring & Alerting"
  ],
  "Technical Lead / Staff Engineer": [
    "System Design & Architecture", "Code Review & Standards",
    "Technical Mentoring", "Cross-functional Collaboration",
    "Performance Optimization", "Technical Roadmapping",
    "Build vs Buy Decisions"
  ],
  "Engineering Manager": [
    "Technical Leadership", "Agile / Scrum Delivery",
    "Team Building & Hiring", "Performance Management",
    "Strategic Planning", "Stakeholder Management",
    "OKR / KPI Setting", "Budget & Resource Planning"
  ],

  // ── Specialized Engineering ───────────────────────────────────────────────
  "Embedded / Firmware Engineer": [
    "C", "C++ (Embedded)", "Rust (Embedded)", "RTOS",
    "Firmware Development", "ARM / RISC-V", "IoT Protocols",
    "Linux Kernel / Drivers", "Hardware Interfaces (SPI / I2C / UART)"
  ],
  "Database Administrator (DBA)": [
    "PostgreSQL", "MySQL", "Oracle", "SQL Server",
    "MongoDB", "Redis", "Cassandra", "DynamoDB",
    "Database Performance Tuning", "Replication / Sharding",
    "Backup & Recovery", "Migration Planning"
  ],
  "Blockchain / Web3 Developer": [
    "Solidity", "Rust (Web3)", "Ethereum / EVM",
    "Web3.js / Ethers.js", "Smart Contract Security",
    "DeFi Protocols", "NFT / Token Standards",
    "Layer 2 Solutions", "Hyperledger"
  ],
  "Game Developer": [
    "Unity / C#", "Unreal Engine / C++", "Godot / GDScript",
    "Graphics Programming (OpenGL / Vulkan / DirectX)",
    "Game Physics", "Multiplayer / Networking",
    "Shader Programming", "Mobile Game Development"
  ],

  // ── Project & Product Management ──────────────────────────────────────────
  "Project Manager (IT)": [
    "Project Planning & Scheduling (MS Project / Smartsheet)",
    "Agile / Scrum / Kanban", "Waterfall / Hybrid Delivery",
    "Risk Management", "Budget & Cost Management",
    "Stakeholder Communication", "RAID Log Management",
    "JIRA / Confluence / Azure DevOps",
    "Change Management", "Vendor Management",
    "PMP / PRINCE2 Certification", "Resource Planning"
  ],
  "Project Coordinator": [
    "Project Scheduling & Tracking",
    "JIRA / Trello / Asana / Monday.com",
    "Meeting Facilitation & Minutes",
    "Risk & Issue Tracking",
    "Status Reporting", "Budget Tracking",
    "Document Management (SharePoint / Confluence)",
    "Agile / Scrum Basics",
    "Stakeholder Communication", "MS Office / Excel"
  ],
  "Program Manager": [
    "Program Portfolio Management",
    "Cross-project Dependency Management",
    "Executive Stakeholder Management",
    "Strategic Roadmapping",
    "Agile at Scale (SAFe / LeSS)",
    "Budget & P&L Oversight",
    "Risk & Governance Frameworks",
    "OKR / KPI Alignment",
    "Change Management", "PgMP / PMP Certification"
  ],
  "Project Controller": [
    "Earned Value Management (EVM)",
    "Cost Estimation & Forecasting",
    "Schedule Performance Index (SPI / CPI)",
    "MS Project / Primavera P6",
    "Budget Variance Analysis",
    "Resource Utilization Tracking",
    "Risk Register Management",
    "PMO Governance & Reporting",
    "Financial Reporting", "Change Control"
  ],
  "Product Manager": [
    "Product Roadmapping", "User Story Writing",
    "Agile / Scrum", "Customer Discovery & Research",
    "Competitive Analysis", "Go-to-Market Strategy",
    "A/B Testing & Metrics (OKRs / KPIs)",
    "JIRA / Confluence / ProductBoard",
    "Stakeholder Management", "Prioritization Frameworks (RICE / MoSCoW)",
    "API / Technical Product Basics", "Data-Driven Decision Making"
  ],
  "Senior Product Manager": [
    "Product Vision & Strategy", "Cross-functional Leadership",
    "Product-Led Growth (PLG)", "Enterprise Product Management",
    "Market Analysis & Positioning",
    "P&L Ownership", "Executive Communication",
    "Platform / API Product Management",
    "Agile at Scale", "Build vs Buy Decisions",
    "Customer Advisory & Evangelism"
  ],
  "Product Engineer": [
    "Full Stack Development (React / Node.js / Python)",
    "Rapid Prototyping", "User Empathy & Product Thinking",
    "API Design", "Analytics Integration",
    "A/B Testing & Feature Flags",
    "System Design", "Technical Roadmapping",
    "Agile / Scrum", "Customer Feedback Loops"
  ],
  "Technical Product Manager": [
    "Product Roadmapping", "Technical Spec Writing",
    "API / Platform Product Management", "Data-Driven Decisions",
    "Agile / Scrum", "Stakeholder Management",
    "User Research", "Competitive Analysis"
  ],
  "Scrum Master / Agile Coach": [
    "Scrum Framework", "Kanban",
    "SAFe (Scaled Agile Framework)", "Sprint Planning & Retrospectives",
    "JIRA / Azure DevOps", "Team Velocity & Burndown",
    "Impediment Removal", "Servant Leadership",
    "Agile Transformation", "Coaching & Facilitation",
    "CSM / PSM Certification"
  ],
};

// ============================================================================
// Role Categories — grouped dropdown display
// ============================================================================
export const ROLE_CATEGORIES = {
  "Software Engineering": [
    "Software Engineer - Backend",
    "Software Engineer - Frontend",
    "Software Engineer - Full Stack",
    "Software Engineer - Mobile (iOS)",
    "Software Engineer - Mobile (Android)",
    "Software Engineer - Mobile (Cross-Platform)",
    "Web Developer"
  ],
  "SAS & Analytics": [
    "SAS Developer",
    "SAS Analyst",
    "ETL Developer",
    "Cognos Developer"
  ],
  "Data, BI & AI": [
    "Data Engineer",
    "Data Scientist",
    "Data Analyst",
    "BI Developer / BI Specialist",
    "Business Analyst (Technical)",
    "ML Engineer / AI Engineer"
  ],
  "QA & Testing": [
    "QA Engineer - Manual",
    "QA Engineer - Automation",
    "QA Engineer / SDET",
    "Performance Test Engineer",
    "QA Lead / QA Manager"
  ],
  "IT Infrastructure": [
    "IT Administrator",
    "System Administrator",
    "Windows Administrator",
    "Linux Administrator",
    "Network Engineer",
    "Network Security Engineer",
    "Systems / Infrastructure Engineer"
  ],
  "Cybersecurity": [
    "Cybersecurity Analyst",
    "Cybersecurity Developer / Engineer",
    "Cybersecurity Architect",
    "Security Engineer"
  ],
  "DevOps & Cloud": [
    "DevOps Engineer",
    "Site Reliability Engineer (SRE)",
    "Cloud Engineer / Cloud Architect",
    "Platform Engineer"
  ],
  "ERP": [
    "SAP ABAP Developer",
    "SAP Functional Consultant",
    "SAP BI / BW Developer",
    "SAP Technical Architect",
    "SAGE ERP Consultant",
    "Oracle ERP Consultant",
    "Microsoft Dynamics Consultant"
  ],
  "CRM": [
    "Salesforce Developer",
    "Salesforce Administrator",
    "Salesforce Analyst / Consultant",
    "HubSpot / CRM Specialist",
    "Sage CRM Consultant"
  ],
  "Web & UI/UX Design": [
    "UI/UX Designer",
    "Graphic Designer",
    "UI Architect / Frontend Architect",
    "UX Engineer"
  ],
  "Architecture & Leadership": [
    "Solutions Architect",
    "Technical Lead / Staff Engineer",
    "Engineering Manager"
  ],
  "Project & Product Management": [
    "Project Manager (IT)",
    "Project Coordinator",
    "Program Manager",
    "Project Controller",
    "Product Manager",
    "Senior Product Manager",
    "Product Engineer",
    "Technical Product Manager",
    "Scrum Master / Agile Coach"
  ],
  "Specialized Engineering": [
    "Embedded / Firmware Engineer",
    "Database Administrator (DBA)",
    "Blockchain / Web3 Developer",
    "Game Developer"
  ]
};

export default ROLE_TECH_MAP;

// ============================================================================
// Smart Role Matching — maps job title keywords to recommended roles
// Used when a requirement is selected to surface relevant roles first
// ============================================================================

const ROLE_KEYWORD_MAP = {
  // Software Engineering
  "backend": ["Software Engineer - Backend"],
  "frontend": ["Software Engineer - Frontend", "UI Architect / Frontend Architect"],
  "full stack": ["Software Engineer - Full Stack"],
  "fullstack": ["Software Engineer - Full Stack"],
  "ios": ["Software Engineer - Mobile (iOS)"],
  "android": ["Software Engineer - Mobile (Android)"],
  "mobile": ["Software Engineer - Mobile (iOS)", "Software Engineer - Mobile (Android)", "Software Engineer - Mobile (Cross-Platform)"],
  "react native": ["Software Engineer - Mobile (Cross-Platform)"],
  "flutter": ["Software Engineer - Mobile (Cross-Platform)"],
  "web developer": ["Web Developer"],

  // SAS & Analytics
  "sas developer": ["SAS Developer"],
  "sas analyst": ["SAS Analyst"],
  "sas": ["SAS Developer", "SAS Analyst"],
  "etl developer": ["ETL Developer"],
  "etl": ["ETL Developer", "Data Engineer"],
  "cognos": ["Cognos Developer"],
  "informatica": ["ETL Developer"],
  "datastage": ["ETL Developer"],
  "ssis": ["ETL Developer"],
  "talend": ["ETL Developer"],

  // Data, BI & AI
  "data engineer": ["Data Engineer"],
  "data scientist": ["Data Scientist"],
  "data analyst": ["Data Analyst"],
  "data science": ["Data Scientist", "Data Engineer"],
  "bi developer": ["BI Developer / BI Specialist"],
  "bi specialist": ["BI Developer / BI Specialist"],
  "business intelligence": ["BI Developer / BI Specialist"],
  "power bi": ["BI Developer / BI Specialist", "Data Analyst"],
  "tableau": ["BI Developer / BI Specialist", "Data Analyst"],
  "business analyst": ["Business Analyst (Technical)"],
  "ml engineer": ["ML Engineer / AI Engineer"],
  "machine learning": ["ML Engineer / AI Engineer", "Data Scientist"],
  "ai engineer": ["ML Engineer / AI Engineer"],
  "genai": ["ML Engineer / AI Engineer"],
  "llm": ["ML Engineer / AI Engineer"],

  // QA & Testing
  "qa manual": ["QA Engineer - Manual"],
  "manual qa": ["QA Engineer - Manual"],
  "manual test": ["QA Engineer - Manual"],
  "qa automation": ["QA Engineer - Automation"],
  "automation qa": ["QA Engineer - Automation"],
  "automation test": ["QA Engineer - Automation"],
  "qa engineer": ["QA Engineer / SDET"],
  "sdet": ["QA Engineer / SDET"],
  "qa": ["QA Engineer / SDET"],
  "quality assurance": ["QA Engineer / SDET"],
  "test engineer": ["QA Engineer / SDET"],
  "performance test": ["Performance Test Engineer"],
  "load test": ["Performance Test Engineer"],
  "qa lead": ["QA Lead / QA Manager"],
  "qa manager": ["QA Lead / QA Manager"],

  // IT Infrastructure
  "it admin": ["IT Administrator"],
  "it administrator": ["IT Administrator"],
  "system admin": ["System Administrator"],
  "sysadmin": ["System Administrator"],
  "windows admin": ["Windows Administrator"],
  "windows administrator": ["Windows Administrator"],
  "linux admin": ["Linux Administrator"],
  "linux administrator": ["Linux Administrator"],
  "network engineer": ["Network Engineer"],
  "network admin": ["Network Engineer"],
  "network security": ["Network Security Engineer"],
  "infrastructure": ["Systems / Infrastructure Engineer", "Cloud Engineer / Cloud Architect"],

  // Cybersecurity
  "cybersecurity analyst": ["Cybersecurity Analyst"],
  "security analyst": ["Cybersecurity Analyst"],
  "soc analyst": ["Cybersecurity Analyst"],
  "cybersecurity developer": ["Cybersecurity Developer / Engineer"],
  "security developer": ["Cybersecurity Developer / Engineer"],
  "cybersecurity architect": ["Cybersecurity Architect"],
  "security architect": ["Cybersecurity Architect"],
  "cybersecurity": ["Cybersecurity Analyst", "Security Engineer"],
  "cyber security": ["Cybersecurity Analyst", "Security Engineer"],
  "security engineer": ["Security Engineer"],
  "penetration": ["Cybersecurity Developer / Engineer"],

  // DevOps & Cloud
  "devops": ["DevOps Engineer", "Site Reliability Engineer (SRE)"],
  "sre": ["Site Reliability Engineer (SRE)", "DevOps Engineer"],
  "cloud": ["Cloud Engineer / Cloud Architect", "DevOps Engineer"],
  "platform engineer": ["Platform Engineer"],

  // ERP
  "sap abap": ["SAP ABAP Developer"],
  "abap": ["SAP ABAP Developer"],
  "sap functional": ["SAP Functional Consultant"],
  "sap consultant": ["SAP Functional Consultant"],
  "sap bi": ["SAP BI / BW Developer"],
  "sap bw": ["SAP BI / BW Developer"],
  "sap architect": ["SAP Technical Architect"],
  "sap technical": ["SAP ABAP Developer", "SAP Technical Architect"],
  "sap": ["SAP Functional Consultant", "SAP ABAP Developer"],
  "sage erp": ["SAGE ERP Consultant"],
  "sage": ["SAGE ERP Consultant", "Sage CRM Consultant"],
  "oracle erp": ["Oracle ERP Consultant"],
  "oracle fusion": ["Oracle ERP Consultant"],
  "dynamics": ["Microsoft Dynamics Consultant"],
  "d365": ["Microsoft Dynamics Consultant"],

  // CRM
  "salesforce developer": ["Salesforce Developer"],
  "salesforce admin": ["Salesforce Administrator"],
  "salesforce analyst": ["Salesforce Analyst / Consultant"],
  "salesforce consultant": ["Salesforce Analyst / Consultant"],
  "salesforce": ["Salesforce Developer", "Salesforce Administrator"],
  "hubspot": ["HubSpot / CRM Specialist"],
  "crm": ["Salesforce Developer", "HubSpot / CRM Specialist"],
  "sage crm": ["Sage CRM Consultant"],

  // Web & UI/UX
  "ui ux": ["UI/UX Designer"],
  "ux designer": ["UI/UX Designer"],
  "ui designer": ["UI/UX Designer"],
  "graphic designer": ["Graphic Designer"],
  "graphic": ["Graphic Designer"],
  "ui architect": ["UI Architect / Frontend Architect"],
  "frontend architect": ["UI Architect / Frontend Architect"],
  "ux engineer": ["UX Engineer"],

  // Architecture & Leadership
  "architect": ["Solutions Architect", "Cloud Engineer / Cloud Architect"],
  "solutions architect": ["Solutions Architect"],
  "tech lead": ["Technical Lead / Staff Engineer"],
  "staff engineer": ["Technical Lead / Staff Engineer"],
  "technical lead": ["Technical Lead / Staff Engineer"],
  "engineering manager": ["Engineering Manager"],

  // Project & Product Management
  "project manager": ["Project Manager (IT)"],
  "project coordinator": ["Project Coordinator"],
  "program manager": ["Program Manager"],
  "project controller": ["Project Controller"],
  "product manager": ["Product Manager", "Technical Product Manager"],
  "senior product manager": ["Senior Product Manager"],
  "product engineer": ["Product Engineer"],
  "tpm": ["Technical Product Manager"],
  "scrum master": ["Scrum Master / Agile Coach"],
  "agile coach": ["Scrum Master / Agile Coach"],

  // Specialized
  "embedded": ["Embedded / Firmware Engineer"],
  "firmware": ["Embedded / Firmware Engineer"],
  "dba": ["Database Administrator (DBA)"],
  "database admin": ["Database Administrator (DBA)"],
  "blockchain": ["Blockchain / Web3 Developer"],
  "web3": ["Blockchain / Web3 Developer"],
  "game": ["Game Developer"],

  // Broad keywords (lower priority)
  "software engineer": ["Software Engineer - Backend", "Software Engineer - Frontend", "Software Engineer - Full Stack"],
  "developer": ["Software Engineer - Backend", "Software Engineer - Frontend", "Software Engineer - Full Stack"],
  ".net": ["Software Engineer - Backend"],
  "java": ["Software Engineer - Backend"],
  "python": ["Software Engineer - Backend", "Data Scientist", "ML Engineer / AI Engineer"],
  "react": ["Software Engineer - Frontend", "Software Engineer - Full Stack"],
  "node": ["Software Engineer - Backend", "Software Engineer - Full Stack"],
};

/**
 * Analyze a job title and return recommended role types.
 * Returns { recommended: [...roleNames], topMatch: null }
 */
export function getRecommendedRoles(jobTitle) {
  if (!jobTitle) return { recommended: [], topMatch: null };

  const title = jobTitle.toLowerCase().trim();
  const matchedRoles = new Map(); // role -> priority (lower = better)

  // Check each keyword pattern against the job title
  Object.entries(ROLE_KEYWORD_MAP).forEach(([keyword, roles]) => {
    if (title.includes(keyword)) {
      // Longer keyword matches get higher priority (more specific)
      const priority = 100 - keyword.length;
      roles.forEach(role => {
        const existing = matchedRoles.get(role) || 999;
        matchedRoles.set(role, Math.min(existing, priority));
      });
    }
  });

  // Sort by priority (most specific match first)
  const sorted = [...matchedRoles.entries()]
    .sort((a, b) => a[1] - b[1])
    .map(([role]) => role);

  return {
    recommended: sorted,
    topMatch: sorted[0] || null
  };
}